function cleanText(value) {
  return typeof value === 'string' ? value.trim() : value;
}

// The same normalisation the mapping step uses (functions/sheetColumnAnalysis.js
// normalizeHeader): case, surrounding and inner whitespace (including the
// non-breaking space) and punctuation never decide whether a header is read.
export function normalizeUploadHeader(header) {
  return String(header || '')
    .normalize('NFKD')
    .toLowerCase()
    .replace(/[’']/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

function readAll(row = {}, key) {
  const wanted = normalizeUploadHeader(key);
  return Object.keys(row)
    .filter(header => normalizeUploadHeader(header) === wanted)
    .map(header => row[header]);
}

function readFirst(row = {}, keys = []) {
  for (const key of keys) {
    for (const raw of readAll(row, key)) {
      const value = cleanText(raw);
      if (value !== undefined && value !== null && value !== '') return value;
    }
  }
  return '';
}

function cleanEmail(value) {
  return typeof value === 'string' ? value.trim() : '';
}

const CONTACT_EMAIL_PAIRS = [
  {
    contactKeys: ['Listing Broker', 'Listing Broker Contact', 'Broker Contact'],
    emailKeys: ['Broker Email'],
  },
  {
    contactKeys: ['Leasing Company Contact', 'Leasing Contact', 'Leasing Agent'],
    emailKeys: ['Leasing Email'],
  },
];

const ALL_CONTACT_KEYS = [
  'Leasing Contact',
  'Leasing Company Contact',
  'Contact',
  'Contact Name',
  'Listing Contact',
  'Leasing Agent',
  'Listing Broker',
  'Broker Contact',
  'Listing Broker Contact',
];

// Spellings of the one generic recipient column. Kept in step with the
// mapping step's `email` aliases (parity test in uploadedPropertyRows.test.js).
const GENERIC_EMAIL_KEYS = ['Email', 'Email Address', 'E-mail', 'E-mail Address', 'Contact Email'];

const IDENTITY_KEYS = {
  property_address: ['Property Address', 'Street Address', 'Address'],
  city: ['City', 'Property City'],
  property_name: ['Property Name', 'Building Name', 'Suite', 'Suite Name'],
  leasing_company: ['Leasing Company Name', 'Leasing Company', 'Brokerage', 'Broker Company'],
  flyer_link: [
    'Flyer / Link', 'Flyer', 'Flyers', 'PDF', 'Brochure Link',
    'Marketing Package Link', 'Listing Link',
  ],
};

function uniqueValues(row, keys, cleanValue, isValid = value => Boolean(value)) {
  const values = [];
  const seen = new Set();

  keys.forEach(key => {
    readAll(row, key).forEach(raw => {
      const value = cleanValue(raw);
      if (!isValid(value)) return;
      const identity = String(value).toLowerCase();
      if (seen.has(identity)) return;
      seen.add(identity);
      values.push(value);
    });
  });

  return values;
}

function readContactEmailPair(row) {
  for (const pair of CONTACT_EMAIL_PAIRS) {
    const contact = cleanText(readFirst(row, pair.contactKeys));
    const email = cleanEmail(readFirst(row, pair.emailKeys));
    if (contact && email.includes('@')) return { contact, email };
  }

  const soleContacts = uniqueValues(row, ALL_CONTACT_KEYS, cleanText);
  const genericEmail = cleanEmail(readFirst(row, GENERIC_EMAIL_KEYS));
  if (genericEmail.includes('@')) {
    return {
      contact: soleContacts.length === 1 ? soleContacts[0] : '',
      email: genericEmail,
    };
  }

  const specializedEmailKeys = CONTACT_EMAIL_PAIRS.flatMap(pair => pair.emailKeys);
  const soleSpecializedEmails = uniqueValues(
    row,
    specializedEmailKeys,
    cleanEmail,
    email => email.includes('@')
  );
  if (soleContacts.length === 1 && soleSpecializedEmails.length === 1) {
    return {
      contact: soleContacts[0],
      email: soleSpecializedEmails[0],
    };
  }

  for (const pair of CONTACT_EMAIL_PAIRS) {
    const email = cleanEmail(readFirst(row, pair.emailKeys));
    if (email.includes('@')) {
      return {
        contact: cleanText(readFirst(row, pair.contactKeys)),
        email,
      };
    }
  }

  return {
    contact: cleanText(readFirst(row, ALL_CONTACT_KEYS)),
    email: '',
  };
}

function mailtoAddress(target) {
  const match = /^\s*mailto:([^?]*)/i.exec(String(target || ''));
  if (!match) return '';
  let decoded = match[1];
  try {
    decoded = decodeURIComponent(decoded);
  } catch (error) {
    // Keep the raw text; the '@' check below still decides.
  }
  const first = decoded.split(/[,;]/)[0].trim();
  return first.includes('@') ? first : '';
}

function isWebUrl(value) {
  return /^(https?:\/\/|www\.)\S+$/i.test(String(value || '').trim());
}

// A recipient that exists only as a mailto link: on the email cell first, then
// on the contact name, then anywhere else on the row.
function emailFromRowLinks(links, emailKeys, contactKeys) {
  if (!links) return '';
  const ordered = [...emailKeys, ...contactKeys]
    .flatMap(key => readAll(links, key));
  const rest = Object.values(links);
  for (const target of [...ordered, ...rest]) {
    const address = mailtoAddress(target);
    if (address) return address;
  }
  return '';
}

// The header the confirmed column mapping names for `canonicalKey`, if this
// tab really has it. A mapping read from another tab (the analysis reads the
// first tab) is ignored rather than blanking every row.
function mappedHeader(mappings, headers, canonicalKey) {
  const header = mappings?.[canonicalKey];
  if (!header || !normalizeUploadHeader(header)) return null;
  if (!Array.isArray(headers)) return header;
  const wanted = normalizeUploadHeader(header);
  return headers.some(candidate => normalizeUploadHeader(candidate) === wanted) ? header : null;
}

function readIdentity(row, options, canonicalKey) {
  const header = mappedHeader(options.mappings, options.headers, canonicalKey);
  const keys = IDENTITY_KEYS[canonicalKey];
  return header ? readFirst(row, [header, ...keys]) : readFirst(row, keys);
}

/**
 * One uploaded row as the campaign reads it.
 *
 * options.mappings — the confirmed column mapping ({ canonicalKey: header },
 * as ColumnMappingStep's handleConfirm returns it). When it names an email
 * column the recipient is read from that column ONLY: it is the column the
 * reviewed-recipient write (campaignWorkbook.js) and the backend's
 * recipient-vs-row check read, so a recipient taken from any other column
 * would be dead-lettered at send.
 * options.headers — the tab's header row, to tell a mapping that belongs to
 * this tab from one that does not.
 * options.links — this row's hyperlink targets by header, for values that
 * exist only in a link (a mailto on the broker's name, a URL behind "Flyer").
 */
export function buildUploadedPropertyFromRow(row = {}, index = 0, options = {}) {
  let { contact, email } = readContactEmailPair(row);

  const emailHeader = mappedHeader(options.mappings, options.headers, 'email');
  if (emailHeader) {
    const mappedEmail = cleanEmail(readFirst(row, [emailHeader]));
    if (mappedEmail.toLowerCase() !== email.toLowerCase()) {
      const contactHeader = mappedHeader(options.mappings, options.headers, 'leasing_contact');
      contact = contactHeader ? cleanText(readFirst(row, [contactHeader])) : '';
    }
    email = mappedEmail;
  }

  // options.links holds each cell's hyperlink target by header (SheetJS
  // cell.l.Target). The visible text wins whenever it is itself an address
  // or URL; a link only fills a value the text does not carry.
  const links = options.links || null;
  let emailFromLink = false;
  if (!email.includes('@')) {
    const contactHeader = mappedHeader(options.mappings, options.headers, 'leasing_contact');
    const linked = emailFromRowLinks(
      links,
      emailHeader ? [emailHeader] : [...GENERIC_EMAIL_KEYS, ...CONTACT_EMAIL_PAIRS.flatMap(pair => pair.emailKeys)],
      contactHeader ? [contactHeader, ...ALL_CONTACT_KEYS] : ALL_CONTACT_KEYS
    );
    if (linked) {
      email = linked;
      emailFromLink = true;
    }
  }

  let flyerLink = readIdentity(row, options, 'flyer_link');
  let flyerFromLink = false;
  if (links && !isWebUrl(flyerLink)) {
    const flyerHeader = mappedHeader(options.mappings, options.headers, 'flyer_link');
    const target = String(readFirst(links, flyerHeader
      ? [flyerHeader, ...IDENTITY_KEYS.flyer_link]
      : IDENTITY_KEYS.flyer_link) || '');
    if (/^https?:\/\//i.test(target)) {
      flyerLink = target;
      flyerFromLink = true;
    }
  }

  return {
    ...row,
    rowIndex: index + 3,
    address: readIdentity(row, options, 'property_address'),
    city: readIdentity(row, options, 'city'),
    propertyName: readIdentity(row, options, 'property_name'),
    leasingCompany: readIdentity(row, options, 'leasing_company'),
    contact,
    email,
    ...(emailFromLink ? { emailFromLink: true } : {}),
    totalSF: readFirst(row, ['Total SF', 'Available SF', 'SF']),
    rentPerSF: readFirst(row, ['Rent/SF/Yr', 'Rent/SF /Yr', 'Rent/SF / Year', 'Asking Rent', 'Rate']),
    flyerLink,
    ...(flyerFromLink ? { flyerFromLink: true } : {}),
  };
}
